import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCountdown } from '@/hooks/useCountdown';
import { queryKeys } from '@/hooks/useAuctions';
import clsx from 'clsx';
import type { AuctionStatus } from '@/types/domain';
import styles from './BigTimer.module.css';

interface Props {
  auctionId: string;
  endsAt: string;
  status?: AuctionStatus;
}

export const BigTimer = ({ auctionId, endsAt, status }: Props) => {
  // Таймер идёт только у активных торгов. Пока лот на модерации/в черновике,
  // endsAt — это лишь предварительная оценка (от создания), реальный отсчёт
  // начнётся после одобрения. Поэтому до active обратный отсчёт не показываем.
  if (status && status !== 'active') {
    const info = STATUS_LABELS[status];
    if (info) {
      return (
        <div className={clsx(styles.wrap, styles.wrapOver)}>
          <p className={styles.label}>{info.label}</p>
          {info.sub && <p className={styles.statusSub}>{info.sub}</p>}
        </div>
      );
    }
  }

  return <ActiveTimer auctionId={auctionId} endsAt={endsAt} status={status} />;
};

const STATUS_LABELS: Partial<
  Record<AuctionStatus, { label: string; sub?: string }>
> = {
  draft: { label: 'Черновик', sub: 'Лот ещё не опубликован' },
  pending_review: {
    label: 'На модерации',
    sub: 'Таймер начнётся после одобрения',
  },
  scheduled: { label: 'Запланирован', sub: 'Скоро стартует' },
  finished: { label: 'Торги завершены' },
  sold: { label: 'Продано' },
  unsold: { label: 'Не продано' },
  cancelled: { label: 'Отменён' },
  rejected: { label: 'Отклонён модератором' },
};

const ActiveTimer = ({
  auctionId,
  endsAt,
  status,
}: {
  auctionId: string;
  endsAt: string;
  status?: AuctionStatus;
}) => {
  const qc = useQueryClient();
  const t = useCountdown(endsAt);
  const waitingForClose = t.isOver && status === 'active';

  // ends_at прошло, но сервер ещё не перевёл лот в finished — подтягиваем статус.
  useEffect(() => {
    if (!waitingForClose) return;

    void qc.invalidateQueries({ queryKey: queryKeys.auction(auctionId) });

    const id = setInterval(() => {
      void qc.invalidateQueries({ queryKey: queryKeys.auction(auctionId) });
    }, 3000);

    return () => clearInterval(id);
  }, [waitingForClose, auctionId, qc]);

  if (waitingForClose) {
    return (
      <div className={clsx(styles.wrap, styles.wrapOver)}>
        <p className={styles.label}>Торги завершены</p>
        <p className={styles.statusSub}>Подводим итоги…</p>
      </div>
    );
  }

  if (t.isOver) {
    return (
      <div className={clsx(styles.wrap, styles.wrapOver)}>
        <p className={styles.label}>Торги завершены</p>
      </div>
    );
  }

  const isUrgent = t.totalMs < 60 * 60 * 1000;
  const isVeryUrgent = t.totalMs < 5 * 60 * 1000;

  return (
    <div className={clsx(
      styles.wrap,
      isUrgent && styles.wrapUrgent,
      isVeryUrgent && styles.wrapVeryUrgent,
    )}>
      <p className={styles.label}>До окончания</p>
      <div className={styles.digits}>
        {t.days > 0 && (
          <>
            <Unit value={t.days} label="дн" />
            <Sep />
          </>
        )}
        <Unit value={t.hours} label="ч" />
        <Sep />
        <Unit value={t.minutes} label="мин" />
        <Sep />
        <Unit value={t.seconds} label="сек" />
      </div>
    </div>
  );
};

const Unit = ({ value, label }: { value: number; label: string }) => (
  <div className={styles.unit}>
    <span className={styles.unitValue}>{String(value).padStart(2, '0')}</span>
    <span className={styles.unitLabel}>{label}</span>
  </div>
);

const Sep = () => <span className={styles.sep}>:</span>;
